import React from 'react';
import { Award, Briefcase, ExternalLink, FileText, CheckCircle } from 'lucide-react';
import { professionalData } from '../professionalData';

// Separate component for credential cards to use hooks properly
const CredentialCard = ({ credential, index }) => {
  const [expanded, setExpanded] = React.useState(false);
  
  return (
    <div 
      className="glass-card"
      style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: expanded ? 'auto' : '250px'
      }}
      data-testid={`credential-card-${index}`}
    >
      <h3 style={{ 
        fontSize: '1.125rem',
        marginBottom: '1rem',
        fontFamily: 'var(--font-body)',
        fontWeight: 600,
        color: 'var(--primary)',
        lineHeight: 1.3
      }}>
        {credential.title}
      </h3>
      
      {expanded ? (
        <div>
          <p style={{ 
            color: 'var(--text-muted)',
            lineHeight: 1.8,
            marginBottom: '1.5rem',
            fontSize: '0.95rem'
          }}>
            {credential.detail}
          </p>
          
          {credential.certificate && (
            <a 
              href={credential.certificate}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.75rem 1.25rem',
                background: 'var(--accent)',
                color: 'white',
                borderRadius: '8px',
                fontSize: '0.875rem',
                fontWeight: 500,
                textDecoration: 'none',
                border: 'none',
                marginBottom: '1rem'
              }}
            >
              <FileText size={16} />
              View Certificate
            </a>
          )}
          
          <button
            onClick={() => setExpanded(false)}
            style={{
              background: 'transparent',
              border: '2px solid var(--accent)',
              color: 'var(--accent)',
              padding: '0.75rem 1.5rem',
              borderRadius: '8px',
              fontSize: '0.875rem',
              fontWeight: 500,
              cursor: 'pointer',
              width: '100%',
              marginTop: 'auto',
              transition: 'all 0.3s ease'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = 'var(--accent)';
              e.currentTarget.style.color = 'white';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = 'var(--accent)';
            }}
          >
            Show Less
          </button>
        </div>
      ) : (
        <>
          <p style={{ 
            color: 'var(--text-muted)',
            lineHeight: 1.6,
            marginBottom: '1.5rem',
            flex: 1,
            fontSize: '0.875rem'
          }}>
            {credential.detail.substring(0, 80)}...
          </p>
          
          <button
            onClick={() => setExpanded(true)}
            style={{
              background: 'transparent',
              border: '2px solid var(--accent)',
              color: 'var(--accent)',
              padding: '0.75rem 1.5rem',
              borderRadius: '8px',
              fontSize: '0.875rem',
              fontWeight: 500,
              cursor: 'pointer',
              width: '100%',
              marginTop: 'auto',
              transition: 'all 0.3s ease'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = 'var(--accent)';
              e.currentTarget.style.color = 'white';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = 'var(--accent)';
            }}
          >
            Know More
          </button>
        </>
      )}
    </div>
  );
};

// Timeline component for clinical experience with expandable details
const TimelineItem = ({ period, role, organization, location, detail, isLast }) => {
  const [isExpanded, setIsExpanded] = React.useState(false);
  
  return (
    <div 
      style={{
        display: 'flex',
        gap: '2rem',
        paddingBottom: isLast ? 0 : '3rem',
        position: 'relative'
      }}
    >
      {/* Timeline Dot & Line */}
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center',
        minWidth: '40px'
      }}>
        <div style={{
          width: '20px',
          height: '20px',
          borderRadius: '50%',
          background: 'var(--accent)',
          border: '4px solid var(--glass-bg)',
          zIndex: 2,
          boxShadow: '0 0 0 4px var(--accent)'
        }} />
        {!isLast && (
          <div style={{
            width: '2px',
            flex: 1,
            background: 'linear-gradient(to bottom, var(--accent), transparent)',
            marginTop: '0.5rem'
          }} />
        )}
      </div>

      {/* Content */}
      <div className="glass-card" style={{ flex: 1, marginTop: '-10px' }}>
        <div style={{ 
          fontSize: '0.875rem',
          fontWeight: 600,
          color: 'var(--accent)',
          marginBottom: '0.5rem',
          letterSpacing: '0.05em'
        }}>
          {period}
        </div>
        <h3 style={{ 
          fontSize: '1.25rem',
          fontFamily: 'var(--font-heading)',
          fontWeight: 600,
          color: 'var(--primary)',
          marginBottom: '0.5rem',
          lineHeight: 1.3
        }}>
          {role}
        </h3>
        <p style={{ 
          fontSize: '1rem',
          fontWeight: 500,
          color: 'var(--text-main)',
          marginBottom: '0.25rem'
        }}>
          {organization}
        </p>
        {location && (
          <p style={{ 
            fontSize: '0.875rem',
            color: 'var(--text-muted)',
            fontStyle: 'italic',
            marginBottom: '1rem'
          }}>
            {location}
          </p>
        )}
        
        {/* Expandable Detail Section */}
        {detail && (
          <>
            {isExpanded && (
              <div 
                style={{
                  marginTop: '1.5rem',
                  padding: '1.5rem',
                  background: 'rgba(255, 255, 255, 0.5)',
                  borderRadius: '8px',
                  fontSize: '0.95rem',
                  lineHeight: 1.8,
                  color: 'var(--text-main)'
                }}
                dangerouslySetInnerHTML={{ __html: detail }}
              />
            )}
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              style={{
                marginTop: '1rem',
                background: 'transparent',
                border: '2px solid var(--accent)',
                color: 'var(--accent)',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                fontSize: '0.875rem',
                fontWeight: 500,
                cursor: 'pointer',
                width: '100%',
                transition: 'all 0.3s ease'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = 'var(--accent)';
                e.currentTarget.style.color = 'white';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = 'var(--accent)';
              }}
            >
              {isExpanded ? 'Show Less' : 'Know More'}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

const AboutPage = () => {
  // Clinical Experience Timeline Data with detailed drop-downs
  const clinicalExperience = [
    {
      period: "Jun 2026 – Present",
      role: "Founder & Clinical Director",
      organization: "Modern Minds Center for Child Psychology & Behavioural Health",
      location: "Lucknow, India",
      detail: "Architected a sovereign clinical institute operating on a tiered 'Closed-Loop Intervention' model. Lead the diagnostic wing by executing high-resolution psychometric mapping via a gold-standard diagnostic stack (<strong>ADOS-2 Modules 1-4</strong>, <strong>WISC-IV/V</strong>, <strong>Vineland-3</strong>, <strong>Conners 4</strong>, <strong>BRIEF-2</strong>) to produce definitive differential diagnoses for ASD, ADHD, and executive functioning deficits. Execute Systems-Based Behavioral Engineering via <strong>Applied Behavior Analysis (ABA)</strong> and <strong>Positive Behaviour Support (PBS)</strong> frameworks."
    },
    {
      period: "Dec 2025 – Apr 2026",
      role: "Clinical Child Psychologist & Behaviour Therapist",
      organization: "Creative Minds Rehabilitation Centre",
      location: null,
      detail: "Spearheaded multimodal psychometric impairments profiling utilizing specialized indigenous tools (<strong>ISAA</strong>, <strong>Com-DEALL Developmental Checklists</strong>) alongside standardized protocols (<strong>M-CHAT-R/F</strong>, <strong>INCLEN</strong>, <strong>Dyslexia Screening Tests</strong>). Delivered evidence-based interventions integrating <strong>ABA principles</strong>, <strong>sensory integration</strong>, and <strong>pivotal response techniques</strong> to remediate deficits in social reciprocity and emotional regulation."
    },
    {
      period: "Aug 2025 – Dec 2025",
      role: "Behaviour Therapy Assistant",
      organization: "Lizard Centre",
      location: "Sydney, Australia",
      detail: "Formulated and executed highly tailored <strong>Positive Behaviour Support (PBS)</strong> strategies and <strong>Behaviour Intervention Plans (BIP)</strong>. Conducted granular, real-time functional behavioral analyses in acute clinical environments to operationalize target behaviors, enhance skill acquisition, and systematically minimize critical challenging behavior episodes in autistic populations."
    },
    {
      period: "Apr 2025 – Jun 2025",
      role: "Assistant Psychologist",
      organization: "Indian Mental Health & Research Centre (IMHRC)",
      location: "Lucknow, India",
      detail: "Completed rigorous supervised clinical training within the high-acuity 'Hausla' early intervention unit. Deployed comprehensive psychometric batteries—including the <strong>Indian Scale for Assessment of Autism (ISAA)</strong>, <strong>ADHD scales</strong>, <strong>Bender Gestalt Test (BGT)</strong>, <strong>Rorschach Exner System</strong>, and <strong>DSM-5 diagnostic tools</strong>. Executed complex case conceptualizations and <strong>CBT-driven psychoeducation</strong> to optimize developmental outcomes for children with special needs."
    },
    {
      period: "Jan 2023 – Dec 2023",
      role: "Psychometric Assessor",
      organization: "Kids & Co. Clinical Psychology",
      location: "Sydney, Australia",
      detail: "Executed high-volume, comprehensive psychometric analysis and detailed clinical case reporting for diverse pediatric populations. Specialized in the rigorous administration and clinical scoring of the <strong>ADOS-2</strong>, integrating granular observational data with broad cognitive and sensory profiling matrices to translate complex psychometry into actionable diagnostic reports for senior medical gatekeepers."
    },
    {
      period: "Jul 2022 – Dec 2022",
      role: "Assistant Psychologist",
      organization: "Vinay Neuro Psychiatry Clinic",
      location: "Lucknow, India",
      detail: "Facilitated <strong>Cognitive Behavioral Therapy (CBT)</strong>-based therapeutic frameworks and administered projective personality assessments (<strong>Rorschach Inkblot Test</strong>) for clients managing substance use disorders and severe emotional dysregulation."
    },
    {
      period: "Jul 2015 – Present",
      role: "Proprietor & Counsellor",
      organization: "Sai Da Healthcare",
      location: null,
      detail: "Direct multidisciplinary wellness operations, managing enterprise-level corporate wellness initiatives. Oversee and implement clinical stress management protocols, pre-employment/pre-insurance health audits, and high-level corporate counseling frameworks."
    },
    {
      period: "Nov 2021 – Dec 2022",
      role: "Behavior Support Advisor",
      organization: "The Sheiling Ringwood School & College",
      location: "United Kingdom",
      detail: "Partnered with SEN multidisciplinary teams to design individualized <strong>PBS behavioral protocols</strong>, achieving a 30% reduction in critical behavioral incidents through structured environmental modifications. Co-facilitated a cross-continental inclusion program leveraging a pioneering music-based outreach model to foster dynamic social connections and social reciprocity outcomes in students with severe intellectual disabilities."
    },
    {
      period: "Nov 2018 – Jan 2023",
      role: "Counseling Psychologist",
      organization: "Varta Health",
      location: "Global Telehealth",
      detail: "Engineered integrative, solution-focused psychotherapy and short-term <strong>CBT/REBT</strong> interventions across global cultural contexts. Pioneered and structured comprehensive Lifestyle Assessment Programs targeting acute anxiety and adjustment disorders."
    }
  ];

  // Categorized Credentials
  const categorizedCredentials = {
    diagnostics: [
      {
        title: "ADOS-2 (Modules 1-4)",
        detail: "Gold standard autism diagnostic tool, certified for all modules with clinical reliability training",
        certificate: null
      },
      {
        title: "Pearson WISC-IV",
        detail: "Wechsler Intelligence Scale for Children - comprehensive cognitive and intellectual assessment certification",
        certificate: null
      },
      {
        title: "ISAA",
        detail: "Indian Scale for Assessment of Autism - culturally-adapted autism spectrum assessment tool",
        certificate: null
      },
      {
        title: "Bender Gestalt (BGT)",
        detail: "Visual-motor integration and neuropsychological assessment for detecting organic brain dysfunction",
        certificate: null
      },
      {
        title: "Rorschach Exner",
        detail: "Projective personality assessment using the comprehensive Exner scoring system for psychological evaluation",
        certificate: null
      },
      {
        title: "NIMHANS Battery",
        detail: "Comprehensive neuropsychological assessment battery from the National Institute of Mental Health and Neurosciences",
        certificate: null
      }
    ],
    behavioral: [
      {
        title: "Registered Behaviour Technician (RBT®)",
        detail: "Board-certified paraprofessional credential in Applied Behavior Analysis for implementing behavior intervention plans",
        certificate: null
      },
      {
        title: "ABA Frameworks",
        detail: "Advanced training in Applied Behavior Analysis methodologies for evidence-based behavioral interventions",
        certificate: null
      },
      {
        title: "Positive Behaviour Support (PBS)",
        detail: "Person-centered approach to supporting individuals with challenging behaviors through environmental and systemic modifications",
        certificate: null
      },
      {
        title: "CBT",
        detail: "Cognitive Behavioral Therapy - evidence-based psychotherapy for treating emotional and behavioral disorders",
        certificate: null
      },
      {
        title: "DBT",
        detail: "Dialectical Behavior Therapy - skills-based approach for emotion regulation and distress tolerance",
        certificate: null
      }
    ],
    neurodiversity: [
      {
        title: "Certified Trauma-Informed Coach",
        detail: "Specialized training in trauma-sensitive approaches to therapeutic intervention and coaching practices",
        certificate: null
      },
      {
        title: "ASDI Professional",
        detail: "Autism Spectrum Disorder certification for comprehensive assessment and intervention planning",
        certificate: null
      },
      {
        title: "PESI Neurodiversity Specialist",
        detail: "Advanced training in neurodiversity-affirming practices and strengths-based approaches to neurodevelopmental differences",
        certificate: null
      },
      {
        title: "Polyvagal Theory",
        detail: "Advanced understanding of autonomic nervous system regulation and its role in trauma, attachment, and social engagement",
        certificate: null
      },
      {
        title: "Sensory Processing",
        detail: "Specialized training in sensory integration theory and assessment of sensory processing patterns in neurodivergent individuals",
        certificate: null
      }
    ]
  };

  return (
    <div>
      {/* SECTION 1: Bio - Bridging Brain Science & Human Connection */}
      <section className="section" style={{ paddingTop: '4rem' }} data-testid="about-hero">
        <div className="container">
          <div className="grid-2" style={{ gap: '4rem', alignItems: 'start' }}>
            <div>
              <p className="uppercase-small" style={{ marginBottom: '1rem' }}>
                About
              </p>
              <h1 style={{ marginBottom: '1rem' }}>
                Bridging Brain Science & Human Connection
              </h1>
              <p style={{
                fontSize: '1.125rem',
                fontWeight: 600,
                lineHeight: 1.5,
                color: 'var(--accent)',
                marginBottom: '2rem',
                fontFamily: 'var(--font-body)'
              }}>
                Child Psychologist & Neuro-Development Specialist | Specialist in Neurodiversity & Autism | Parenting Consultant
              </p>
              
              {/* Pull-Quote Style Manifesto */}
              <div style={{
                position: 'relative',
                paddingLeft: '2rem',
                marginBottom: '2rem'
              }}>
                {/* Vertical Copper Bar */}
                <div style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: '4px',
                  background: '#B87333'
                }} />
                
                {/* Pull-Quote Text */}
                <p style={{
                  fontFamily: 'var(--font-heading)',
                  fontStyle: 'italic',
                  fontSize: '1.35rem',
                  lineHeight: 1.8,
                  color: 'var(--text-main)'
                }}>
                  Behind every diagnostic label is a child with a unique story and a family looking for a bridge to understanding. In pivotal moments of change—whether it&apos;s a child&apos;s first words after months of tailored intervention, or a family&apos;s daily routine finally finding harmony—I have seen the profound impact of evidence-based precision. My work is dedicated to turning complex neurodevelopmental challenges into actionable progress for every child I serve.
                  <br /><br />
                  This clinical foundation is powered by a triple-continent academic pedigree spanning the USA, UK, and Australia, allowing me to integrate advanced neurobiological insights—from the Master of Brain and Mind Sciences (University of Sydney) and MSc in Psychology (UK)—with highly personalized, culturally-responsive care that goes beyond the standard clinical baseline.
                </p>
              </div>
            </div>
            <div style={{ 
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}>
              <img 
                src="/isha-headshot-new.jpg"
                alt="Isha Rastogi - Child Psychologist & Neuro-Development Specialist"
                style={{ 
                  width: '100%',
                  maxWidth: '500px',
                  borderRadius: '12px',
                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)'
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 2: Founder Spotlight - Modern Minds */}
      <section className="section" style={{ paddingTop: '2rem', paddingBottom: '4rem' }} data-testid="founder-spotlight">
        <div className="container">
          <div className="glass-card-strong" style={{ 
            maxWidth: '900px', 
            margin: '0 auto',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            padding: '3rem'
          }}>
            <div style={{
              width: '120px',
              height: '120px',
              marginBottom: '2rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <img 
                src="/Isha Logo.jpg"
                alt="Modern Minds Logo"
                style={{
                  width: '100%',
                  height: 'auto'
                }}
              />
            </div>
            <h2 style={{ 
              fontSize: '2rem',
              marginBottom: '0.5rem',
              color: 'var(--primary)',
              fontFamily: 'var(--font-heading)'
            }}>
              Founder & Clinical Director, Modern Minds
            </h2>
            <p style={{
              fontSize: '1.125rem',
              fontWeight: 500,
              color: 'var(--accent)',
              marginBottom: '2rem'
            }}>
              Center for Child Psychology, Behavioural Health & Parenting Consultancy
            </p>
            <p style={{
              fontSize: '1.125rem',
              lineHeight: 1.8,
              color: 'var(--text-main)',
              marginBottom: '2rem',
              maxWidth: '700px',
              textAlign: 'left'
            }}>
              In the Indian clinical landscape, pediatric assessments are too often reduced to static checklists and defensive parent-reporting. At Modern Minds, we operate on a strictly Observer-Led clinical model. We deploy The Grassroots Psychological Protocol—a proprietary clinical methodology that bridges the gap between raw psychometric data and observable, real-world nervous system load. We do not offer generic advice; we provide families and referring physicians with an empirical roadmap from diagnostic overload to sustained developmental competence.
            </p>
            <a 
              href={professionalData.clinic.website}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
              data-testid="founder-clinic-link"
            >
              Visit Modern Minds Clinic
              <ExternalLink size={18} />
            </a>
          </div>
        </div>
      </section>

      {/* SECTION 3: Clinical Leadership & Experience - Vertical Timeline */}
      <section className="section-alt" data-testid="clinical-experience-section">
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <Briefcase size={48} color="var(--accent)" style={{ margin: '0 auto 1rem' }} />
            <h2>Clinical Leadership & Experience</h2>
            <p style={{ 
              fontSize: '1.125rem',
              color: 'var(--text-muted)',
              maxWidth: '700px',
              margin: '1rem auto 0'
            }}>
              Global clinical experience across three continents
            </p>
          </div>

          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            {clinicalExperience.map((experience, index) => (
              <TimelineItem
                key={index}
                period={experience.period}
                role={experience.role}
                organization={experience.organization}
                location={experience.location}
                detail={experience.detail}
                isLast={index === clinicalExperience.length - 1}
              />
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 4: Professional Credentials - 3 Categorized Columns */}
      <section className="section" data-testid="credentials-section">
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <Award size={48} color="var(--accent)" style={{ margin: '0 auto 1rem' }} />
            <h2>Professional Credentials</h2>
            <p style={{ 
              fontSize: '1.125rem',
              color: 'var(--text-muted)',
              maxWidth: '700px',
              margin: '1rem auto 0'
            }}>
              Specialized certifications across diagnostics, behavioral therapy, and neurodiversity
            </p>
          </div>

          {/* Three Categorized Columns */}
          <div className="grid-3" style={{ gap: '3rem', alignItems: 'start' }}>
            {/* Column 1: Diagnostics */}
            <div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                marginBottom: '2rem',
                paddingBottom: '1rem',
                borderBottom: '2px solid var(--accent)'
              }}>
                <CheckCircle size={24} color="var(--accent)" />
                <h3 style={{
                  fontSize: '1.5rem',
                  fontFamily: 'var(--font-heading)',
                  fontWeight: 600,
                  color: 'var(--primary)',
                  margin: 0
                }}>
                  Diagnostics
                </h3>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                {categorizedCredentials.diagnostics.map((credential, index) => (
                  <CredentialCard 
                    key={`diag-${index}`} 
                    credential={credential} 
                    index={`diag-${index}`} 
                  />
                ))}
              </div>
            </div>

            {/* Column 2: Behavioral */}
            <div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                marginBottom: '2rem',
                paddingBottom: '1rem',
                borderBottom: '2px solid var(--accent)'
              }}>
                <CheckCircle size={24} color="var(--accent)" />
                <h3 style={{
                  fontSize: '1.5rem',
                  fontFamily: 'var(--font-heading)',
                  fontWeight: 600,
                  color: 'var(--primary)',
                  margin: 0
                }}>
                  Behavioral
                </h3>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                {categorizedCredentials.behavioral.map((credential, index) => (
                  <CredentialCard 
                    key={`behav-${index}`} 
                    credential={credential} 
                    index={`behav-${index}`} 
                  />
                ))}
              </div>
            </div>

            {/* Column 3: Neurodiversity */}
            <div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                marginBottom: '2rem',
                paddingBottom: '1rem',
                borderBottom: '2px solid var(--accent)'
              }}>
                <CheckCircle size={24} color="var(--accent)" />
                <h3 style={{
                  fontSize: '1.5rem',
                  fontFamily: 'var(--font-heading)',
                  fontWeight: 600,
                  color: 'var(--primary)',
                  margin: 0
                }}>
                  Neurodiversity
                </h3>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                {categorizedCredentials.neurodiversity.map((credential, index) => (
                  <CredentialCard 
                    key={`neuro-${index}`} 
                    credential={credential} 
                    index={`neuro-${index}`} 
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Professional Affiliations */}
      <section className="section-alt" data-testid="affiliations-section">
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <h2>Professional Affiliations</h2>
            <p style={{ 
              fontSize: '1.125rem',
              color: 'var(--text-muted)',
              maxWidth: '700px',
              margin: '1rem auto 0'
            }}>
              Member of leading international psychological associations
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '2rem',
            maxWidth: '900px',
            margin: '0 auto'
          }}>
            {professionalData.affiliations.map((affiliation, index) => (
              <div 
                key={index}
                className="card"
                style={{
                  textAlign: 'center',
                  padding: '2rem',
                  backgroundColor: 'var(--background)'
                }}
                data-testid={`affiliation-${index}`}
              >
                <div style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  backgroundColor: 'var(--primary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1.5rem',
                  fontFamily: 'var(--font-heading)',
                  fontSize: '1.5rem',
                  fontWeight: 700,
                  color: 'white'
                }}>
                  {affiliation.logo}
                </div>
                <h3 style={{ 
                  fontSize: '1.125rem',
                  fontFamily: 'var(--font-body)',
                  fontWeight: 600,
                  marginBottom: '0.5rem',
                  color: 'var(--primary)'
                }}>
                  {affiliation.name}
                </h3>
                <p style={{ 
                  fontSize: '0.875rem',
                  color: 'var(--text-muted)'
                }}>
                  {affiliation.role}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Regulatory Compliance Note */}
      <section className="section" style={{ textAlign: 'center' }} data-testid="compliance-section">
        <div className="container">
          <div style={{
            maxWidth: '800px',
            margin: '0 auto',
            padding: '2rem',
            backgroundColor: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: '0.5rem'
          }}>
            <p style={{ 
              fontSize: '0.95rem',
              lineHeight: 1.8,
              color: 'var(--text-muted)'
            }}>
              <strong style={{ color: 'var(--primary)' }}>Global Regulatory Compliance:</strong> My clinical practice, psychometric administration, and therapeutic interventions are strictly regulated by and adhere to the rigorous ethical frameworks of the American Psychological Association (APA), the British Psychological Society (BPS), and the Australian Psychological Society (APS).
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
